
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Eye, EyeOff, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { authApi } from "@/integrations/mysql/client";

interface LoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

export function LoginForm({
  email,
  setEmail,
  password,
  setPassword,
  isLoading,
  onSubmit
}: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState<"login" | "forgot" | "sent">("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError("");
    setForgotLoading(true);
    try {
      await authApi.forgotPassword(forgotEmail);
      setView("sent");
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : "Erro ao enviar email.");
    } finally {
      setForgotLoading(false);
    }
  };

  if (view === "sent") {
    return (
      <div className="max-w-md mx-auto">
        <Card>
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-12 h-12 text-brand-primary mx-auto mb-4" />
            <h3 className="font-bold font-primary text-gray-800 text-lg mb-2">Email enviado!</h3>
            <p className="text-gray-500 text-sm font-primary mb-6">
              Se o email <strong>{forgotEmail}</strong> estiver cadastrado, você receberá as instruções de redefinição em alguns minutos.
            </p>
            <button
              onClick={() => { setView("login"); setForgotEmail(""); }}
              className="text-brand-primary font-semibold font-primary text-sm hover:underline"
            >
              Voltar ao login
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (view === "forgot") {
    return (
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="w-5 h-5 text-brand-primary" />
              Recuperar senha
            </CardTitle>
            <p className="text-gray-500 text-sm font-primary">
              Informe seu email e enviaremos um link para redefinir a senha.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                />
              </div>
              {forgotError && (
                <p className="text-red-500 text-xs font-primary">{forgotError}</p>
              )}
              <Button
                type="submit"
                className="w-full bg-brand-primary hover:bg-brand-primary/90"
                disabled={forgotLoading}
              >
                {forgotLoading ? "Enviando..." : "Enviar link de recuperação"}
              </Button>
              <button
                type="button"
                onClick={() => setView("login")}
                className="w-full flex items-center justify-center gap-1 text-gray-500 text-sm font-primary hover:text-brand-primary transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao login
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-center flex items-center justify-center gap-2">
            <LogIn className="w-5 h-5" />
            Entrar na Sua Conta
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Senha</Label>
                <button
                  type="button"
                  onClick={() => setView("forgot")}
                  className="text-xs text-brand-primary font-primary hover:underline"
                >
                  Esqueci minha senha
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={isLoading}
            >
              {isLoading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
